import * as React from 'react';
import { useEffect, useContext, useMemo, useState } from 'react';
import {
    Button,
    Checkbox,
    Container,
        Dialog,
    InputAdornment,
    TextField,
  Typography,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';

import { ProductContext } from '../store/productContext';
import loadingProducts from './loadingProducts';
import ProductForm from './ProductForm';
import ProductRow from './ProductRow';

const ProductList: React.FC = () => {
  const {
    getProducts,
        products,
        getProductsLoading,
      addProduct,
      addProductLoading,
        deleteProduct,
        deleteProductLoading,
  } = useContext(ProductContext);
  const theme = useTheme();
  const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);
    const [addProductOpen, setAddProductOpen] = useState(false);

    const data = getProductsLoading ? loadingProducts : products;
    const filteredData = useMemo(() => {
        const normalizedSearchTerm = searchTerm.toLowerCase();

        if (!normalizedSearchTerm) {
            return data;
        }

        return data.filter((product) =>
            product.name?.toLowerCase().includes(normalizedSearchTerm)
            || product.code?.toLowerCase().includes(normalizedSearchTerm)
            || product.description?.toLowerCase().includes(normalizedSearchTerm)
            || product.dimensions?.toLowerCase().includes(normalizedSearchTerm)
            || product.unit?.toLowerCase().includes(normalizedSearchTerm)
        );
    }, [data, searchTerm]);

    const allFilteredSelected = filteredData.length > 0 && filteredData.every((product) => selectedProductIds.includes(product.id));
    const someFilteredSelected = filteredData.some((product) => selectedProductIds.includes(product.id));

    const handleToggleSelectAll = () => {
        if (allFilteredSelected) {
            setSelectedProductIds((current) => current.filter((id) => !filteredData.some((product) => product.id === id)));
            return;
        }

        setSelectedProductIds((current) => {
            const nextIds = new Set(current);
            filteredData.forEach((product) => nextIds.add(product.id));
            return Array.from(nextIds);
        });
    };

    const handleToggleProductSelect = (productId: number) => {
        setSelectedProductIds((current) => (
            current.includes(productId)
                ? current.filter((id) => id !== productId)
                : [...current, productId]
        ));
    };

    const handleRemoveSelected = async () => {
        const idsToDelete = [...selectedProductIds];
        if (idsToDelete.length === 0) {
            return;
        }

        await Promise.all(idsToDelete.map((id) => deleteProduct(id)));
        setSelectedProductIds([]);
        getProducts(true);
    };

    const handleAddProductSubmit = (data: ProductFormDto) => {
        addProduct(data, () => {
            setAddProductOpen(false);
            getProducts(true);
        });
    };

  useEffect(() => {
    getProducts();
  }, [getProducts]);

  return (
        <Container maxWidth="lg">
            <Typography
                align="left"
                variant="h3"
                sx={(currentTheme) => ({
                    ...currentTheme.typography.listTitle,
                    color: currentTheme.palette.titleColor,
                    [currentTheme.breakpoints.down('md')]: {
                        fontSize: '2.5rem',
                    },
                })}
            >
                {t('nav:t.products')}
            </Typography>

            <Grid container alignItems="stretch" sx={{ border: theme.palette.border }}>
                <Grid size={{ xs: 12 }}>
                    <Grid
                        container
                        alignItems="center"
                        sx={{
                            backgroundColor: theme.palette.headerBackground,
                            px: 2,
                            height: 50,
                            borderBottom: theme.palette.border,
                        }}
                    >
                        <Checkbox
                            checked={allFilteredSelected}
                            indeterminate={!allFilteredSelected && someFilteredSelected}
                            onChange={handleToggleSelectAll}
                            size="small"
                            sx={{ mr: 1, color: 'green', '&.Mui-checked': { color: 'green' } }}
                        />

                        <Typography sx={{ ...theme.typography.usersFontTitle }}>
                            Select all
                        </Typography>

                        {selectedProductIds.length > 0 && (
                            <Button
                                sx={{ ml: 2, color: 'green' }}
                                onClick={handleRemoveSelected}
                                disabled={deleteProductLoading}
                            >
                                Remove selected: {selectedProductIds.length}
                            </Button>
                        )}

                        <Grid container alignItems="center" sx={{ ml: 'auto', gap: 1.5 }}>
                            <Grid sx={{ width: 220 }}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    placeholder={'Find product...'}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    sx={{
                                        mt: 0.5,
                                        width: '100%',
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: 'rgba(0, 0, 0, 0.23)',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'rgba(0, 0, 0, 0.87)',
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            ...theme.typography.usersFontRegular,
                                            fontSize: '0.8rem',
                                            py: 0.5,
                                        },
                                    }}
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <SearchIcon sx={{ fontSize: '1rem', color: 'green' }} />
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                            </Grid>

                            <Button
                                sx={{ minWidth: 'auto', p: 0, color: 'green', height: 24 }}
                                onClick={() => setAddProductOpen(true)}
                            >
                                <AddIcon />
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid container size={{ xs: 12 }} alignItems="stretch" sx={{ borderBottom: theme.palette.border }}>
                    <Grid size={{ xs: 2 }} sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{ ...theme.typography.columnHeader, color: theme.palette.primary.main }}
                        >
                            {t('products:t.product')}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 2 }} sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{ ...theme.typography.columnHeader, color: theme.palette.primary.main }}
                        >
                            {t('products:t.description')}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 2 }} sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{ ...theme.typography.columnHeader, color: theme.palette.primary.main }}
                        >
                            {t('products:t.group')}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 2 }} sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{ ...theme.typography.columnHeader, color: theme.palette.primary.main }}
                        >
                            {t('products:t.dimensions')}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 2 }} sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{ ...theme.typography.columnHeader, color: theme.palette.primary.main }}
                        >
                            {t('products:t.unit')}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 2 }} sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{ ...theme.typography.columnHeader, color: theme.palette.primary.main }}
                        >
                            {t('products:t.code')}
                        </Typography>
                    </Grid>
                </Grid>

                {filteredData.map((product) => (
                    <ProductRow
                        key={product.id}
                        product={product}
                        loading={getProductsLoading}
                        selected={selectedProductIds.includes(product.id)}
                        onToggleSelect={handleToggleProductSelect}
                    />
                ))}
            </Grid>

            <Dialog open={addProductOpen} onClose={() => setAddProductOpen(false)}>
                <ProductForm
                    onSubmit={handleAddProductSubmit}
                    buttonText={t('common:t.add')}
                    loading={addProductLoading}
                    cancelButton
                    onCancel={() => setAddProductOpen(false)}
                />
            </Dialog>
        </Container>
  );
};

export default ProductList;
